import React from "react";

import { MailboxGoogleOAuthPanel } from "../oauth/MailboxGoogleOAuthPanel";
import { EmailRetentionPopoverSelect } from "./EmailRetentionPopoverSelect";
import { HELP_GOOGLE_ACCESS_URL } from "../../config/googleOAuthPublic";

function FeatureIconBolt({ className }) {
  return (
    <svg className={className} width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FeatureIconShield({ className }) {
  return (
    <svg className={className} width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FeatureIconControl({ className }) {
  return (
    <svg className={className} width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h10M4 17h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="18" cy="7" r="2" fill="currentColor" />
      <circle cx="8" cy="12" r="2" fill="currentColor" />
      <circle cx="12" cy="17" r="2" fill="currentColor" />
    </svg>
  );
}

function ConnectedMailboxRow({ email, showRemove, onRemove, removePending }) {
  return (
    <div
      className="inline-flex max-w-[min(100%,18rem)] shrink-0 items-center gap-2 rounded-full border border-theme-border px-3 py-1.5"
      role="status"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-theme-accent opacity-60" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-theme-accent" />
      </span>
      <span className="truncate font-mono text-[11px] text-theme-text" title={email}>
        {email}
      </span>
      {showRemove ? (
        <button
          type="button"
          className="shrink-0 rounded px-1 font-mono text-[13px] leading-none text-theme-muted hover:text-theme-text hover:opacity-90 disabled:opacity-40"
          aria-label="Remove this mailbox"
          disabled={removePending}
          onClick={onRemove}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

const INTEGRATION_FEATURES = [
  {
    title: "Fast",
    subtitle: "One Google sign-in and your inbox is ready to scan.",
    Icon: FeatureIconBolt,
  },
  {
    title: "Secure",
    subtitle: "OAuth only. We never handle your Google password.",
    Icon: FeatureIconShield,
  },
  {
    title: "You stay in control",
    subtitle: "Choose retention and disconnect any time in settings.",
    Icon: FeatureIconControl,
  },
];

export function OnboardingGmailStep({
  formState,
  connectedMailboxes,
  onContinueToGoogle,
  mailboxOAuthLoading,
  onEmailRetentionChange,
  retentionPersistPending,
  onDisconnectMailbox,
  disconnectMailboxPendingHash,
}) {
  const hasConnected = connectedMailboxes.length > 0;
  const showRemoveMailbox = connectedMailboxes.length > 1;

  return (
    <section className="space-y-8">
      <div>
        <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background mb-2">
          Connect Gmail
        </h1>
        <p className="font-mono text-[12px] text-theme-muted max-w-xl mb-1">
          FlipMail reads inbound mail to find pitches and can draft replies in Gmail.
        </p>
        <p className="font-mono text-[12px] text-theme-muted max-w-xl">
          Connect a mailbox to finish setup.{" "}
          <a
            href={HELP_GOOGLE_ACCESS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-accent underline underline-offset-2 hover:opacity-90"
          >
            How we use Gmail data
          </a>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {INTEGRATION_FEATURES.map(({ title, subtitle, Icon }) => (
          <div key={title} className="px-0 py-1 text-left sm:py-0">
            <Icon className="mb-3 text-theme-accent" />
            <h2 className="font-display font-bold text-base text-theme-on-background mb-1">{title}</h2>
            <p className="font-mono text-[11px] leading-relaxed text-theme-muted">{subtitle}</p>
          </div>
        ))}
      </div>

      <EmailRetentionPopoverSelect
        value={formState.email_retention_policy}
        onChange={onEmailRetentionChange}
        disabled={retentionPersistPending}
      />

      <div className="max-w-md">
        <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-theme-dim mb-2">Gmail Accounts</p>

        {hasConnected ? (
          <div className="flex flex-wrap items-center gap-2">
            {connectedMailboxes.map((m) => (
              <ConnectedMailboxRow
                key={m.hash}
                email={m.email}
                showRemove={showRemoveMailbox}
                removePending={disconnectMailboxPendingHash === m.hash}
                onRemove={() => onDisconnectMailbox(m.hash)}
              />
            ))}
            <MailboxGoogleOAuthPanel
              className="shrink-0"
              onContinueToGoogle={onContinueToGoogle}
              loading={mailboxOAuthLoading}
              buttonText="Add another"
              buttonSize="compact"
            />
          </div>
        ) : (
          <MailboxGoogleOAuthPanel
            onContinueToGoogle={onContinueToGoogle}
            loading={mailboxOAuthLoading}
            buttonText="Continue with Google"
          />
        )}
      </div>
    </section>
  );
}
